/*
 * blog-main.js — combined-view AJAX pagination.
 *
 * Loaded by sections/main-blog.liquid ONLY when the section is
 * rendering its combined view (page.blog.json template) with
 * `enable_api_loading` on. The native blog index (/blogs/<handle>)
 * uses Shopify's `{% paginate %}` + snippets/pagination.liquid +
 * snippets/ajaxinate.liquid — this module isn't involved there.
 *
 * What it does:
 *   1. Reads the grid's data attributes (total articles,
 *      current page, paginate_by, pagination_type) plus the
 *      initial skeleton placeholders the Liquid emitted.
 *   2. Fetches `/search?view=ajax&q=*&type=article&sort_by=
 *      created-desc&page=N` for the current page, replaces
 *      the skeletons with real `.blog-main__article` cards
 *      pulled from the response.
 *   3. Wires Load More button or numbered pagination
 *      depending on pagination_type, fetching new pages on
 *      click and either appending (load_more) or replacing
 *      (pages) the grid contents.
 *
 * The ajax view template lives at templates/search.ajax.liquid;
 * it renders only `.blog-main__article` wrappers — no chrome.
 * The wrapper class is shared with the initial skeleton +
 * server-side card markup so the same selector works for
 * extraction and swap.
 */

const SELECTOR = '.blog-main[data-api-enabled="true"]';
const INSTANCE_KEY = '_blogMainInstance';

class BlogMain {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('.blog-main__grid');
    if (!this.grid) return;

    this.totalArticles = parseInt(this.grid.dataset.totalArticles, 10) || 0;
    this.paginateBy = parseInt(this.grid.dataset.paginateBy, 10) || 12;
    this.paginationType = this.grid.dataset.paginationType || 'load_more';
    this.totalPages = Math.max(1, Math.ceil(this.totalArticles / this.paginateBy));

    const params = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(params.get('page'), 10) || 1;
    if (this.currentPage < 1) this.currentPage = 1;
    if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;

    this.paginationContainer = root.querySelector('.blog-main__pagination-mount');

    this.init();
  }

  init() {
    // Initial paint — replace skeletons with the current page.
    this.fetchPage(this.currentPage)
      .then((cards) => this.swapGrid(cards))
      .then(() => this.wirePagination());
  }

  wirePagination() {
    if (this.paginationType === 'load_more') {
      this.renderLoadMore();
    } else {
      this.renderPageNumbers();
    }
  }

  // ---------- Fetching --------------------------------------
  // /search?view=ajax... returns chromeless markup containing
  // .blog-main__article wrappers. Parse, return as a NodeList-
  // like array.
  async fetchPage(page) {
    const url = `/search?view=ajax&q=*&type=article&sort_by=created-desc&page=${page}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Search ajax fetch failed: HTTP ${response.status}`);
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return Array.from(doc.querySelectorAll('.blog-main__article'));
  }

  // ---------- Grid swaps ------------------------------------
  swapGrid(cards) {
    this.grid.replaceChildren(...cards);
    if (cards.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'blog-main__empty';
      empty.textContent = 'No articles found.';
      this.grid.appendChild(empty);
    }
  }

  appendGrid(cards) {
    // Remove any trailing skeletons before appending real cards.
    this.grid
      .querySelectorAll('.blog-main__article--skeleton')
      .forEach((el) => el.remove());
    cards.forEach((card) => this.grid.appendChild(card));
  }

  renderSkeletons(count) {
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const wrap = document.createElement('div');
      wrap.className = 'blog-main__article blog-main__article--skeleton';
      wrap.innerHTML = `
        <div class="blog-main__skeleton-image"></div>
        <div class="blog-main__skeleton-text blog-main__skeleton-text--title"></div>
        <div class="blog-main__skeleton-text blog-main__skeleton-text--short"></div>
        <div class="blog-main__skeleton-text"></div>
      `;
      fragment.appendChild(wrap);
    }
    return fragment;
  }

  // ---------- Load More -------------------------------------
  renderLoadMore() {
    if (!this.paginationContainer) return;
    this.paginationContainer.innerHTML = '';

    if (this.totalPages <= 1) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className =
      'blog-main__load-more button ' +
      (this.root.dataset.buttonStyle || 'primary') +
      '-button ' +
      (this.root.dataset.buttonStyle || 'primary') +
      '-button--' +
      (this.root.dataset.buttonScheme || 'dark');
    const minWidth = this.root.dataset.buttonMinWidth;
    if (minWidth) button.style.minWidth = minWidth;
    button.dataset.nextPage = String(this.currentPage + 1);

    const label = document.createElement('span');
    label.className = 'button__text';
    label.textContent = 'Load More';
    button.appendChild(label);

    button.addEventListener('click', () => {
      const nextPage = parseInt(button.dataset.nextPage, 10);
      if (Number.isNaN(nextPage) || nextPage > this.totalPages) {
        button.style.display = 'none';
        return;
      }
      label.textContent = 'Loading…';
      const skeletons = this.renderSkeletons(this.paginateBy);
      this.grid.appendChild(skeletons);

      this.fetchPage(nextPage)
        .then((cards) => {
          this.appendGrid(cards);
          button.dataset.nextPage = String(nextPage + 1);
          label.textContent = 'Load More';
          if (nextPage >= this.totalPages || cards.length === 0) {
            button.style.display = 'none';
          }
        })
        .catch(() => {
          // Network failure — clear skeletons + restore label.
          this.grid
            .querySelectorAll('.blog-main__article--skeleton')
            .forEach((el) => el.remove());
          label.textContent = 'Load More';
        });
    });

    this.paginationContainer.appendChild(button);
  }

  // ---------- Numbered pages --------------------------------
  renderPageNumbers() {
    if (!this.paginationContainer) return;
    if (this.totalPages <= 1) {
      this.paginationContainer.innerHTML = '';
      return;
    }
    this.paginationContainer.innerHTML = this.paginationHtml(this.currentPage);
    this.paginationContainer.querySelectorAll('[data-page]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const targetPage = parseInt(link.dataset.page, 10);
        if (Number.isNaN(targetPage)) return;
        this.goToPage(targetPage);
      });
    });
  }

  paginationHtml(current) {
    const total = this.totalPages;
    const parts = [];
    parts.push('<nav class="pagination" role="navigation"><ul class="pagination__list list-unstyled" role="list">');

    if (current > 1) {
      parts.push(`<li><a href="?page=${current - 1}" data-page="${current - 1}" class="pagination__item pagination__item--prev link button"><span class="button__text">Prev</span></a></li>`);
    }

    const start = Math.max(1, current - 2);
    const end = Math.min(total, current + 2);

    if (start > 1) {
      parts.push(`<li><a href="?page=1" data-page="1" class="pagination__item link button"><span class="button__text">1</span></a></li>`);
      if (start > 2) parts.push('<li><span class="pagination__item button">…</span></li>');
    }

    for (let i = start; i <= end; i++) {
      if (i === current) {
        parts.push(`<li><span class="pagination__item pagination__item--current button"><span class="button__text">${i}</span></span></li>`);
      } else {
        parts.push(`<li><a href="?page=${i}" data-page="${i}" class="pagination__item link button"><span class="button__text">${i}</span></a></li>`);
      }
    }

    if (end < total) {
      if (end < total - 1) parts.push('<li><span class="pagination__item button">…</span></li>');
      parts.push(`<li><a href="?page=${total}" data-page="${total}" class="pagination__item link button"><span class="button__text">${total}</span></a></li>`);
    }

    if (current < total) {
      parts.push(`<li><a href="?page=${current + 1}" data-page="${current + 1}" class="pagination__item pagination__item--next link button"><span class="button__text">Next</span></a></li>`);
    }

    parts.push('</ul></nav>');
    return parts.join('');
  }

  goToPage(page) {
    this.currentPage = page;
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    if (!window.Shopify || !window.Shopify.designMode) {
      window.history.pushState({}, '', url);
    }

    // Replace grid with skeletons while fetching.
    this.grid.replaceChildren();
    this.grid.appendChild(this.renderSkeletons(this.paginateBy));

    this.fetchPage(page)
      .then((cards) => {
        this.swapGrid(cards);
        this.renderPageNumbers();
        const top = this.grid.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top, behavior: 'smooth' });
      })
      .catch(() => {
        this.grid.replaceChildren();
        const err = document.createElement('p');
        err.className = 'blog-main__empty';
        err.textContent = 'Could not load articles.';
        this.grid.appendChild(err);
      });
  }
}

function initAll(scope) {
  const root = scope || document;
  root.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new BlogMain(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (event) => initAll(event.target));
